import { CLASS_NAMES, EVENTS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the Tabbed Content module
 */
export default class TabComponent extends MessageBus {
  /**
   * Constructor function of the TabComponent class.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    /**
     * DOM node that is passed into the constructor
     * @property {object} element DOM node that is passed into the constructor
     */
    this.element = element;

    // Initialize the view
    this.init();
  }

  /**
   * Initializes the view
   * @returns {object} TabComponent A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();
    return this;
  }

  /**
   * Cache DOM References
   * @returns {object} TabComponent A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.tabs = this.element.querySelector(`.${CLASS_NAMES.TABS}`);
    this.tabButtons = this.tabs?.querySelectorAll(
      `${CLASS_NAMES.TABS_BUTTONS}`
    );
    this.items = this.element.querySelectorAll(`.${CLASS_NAMES.ITEM}`);
    this.tabbedContentMain = this.element.querySelector(
      `.${CLASS_NAMES.TAB_CONTENT_MAIN}`
    );
    this.isMobile = window.innerWidth < 768;

    return this;
  }

  /**
   * Create event handlers to enable interaction with view
   * @returns {object} TabComponent A reference to the current instance of the class
   * @chainable
   */
  enable() {
    // Set initial active states
    if (this.items.length) {
      this.items[0].classList.add(`${CLASS_NAMES.ACTIVE}`);
    }

    if (!this.isMobile && this.tabButtons?.length) {
      this.tabButtons[0].classList.add(`${CLASS_NAMES.ACTIVE}`);
    }

    this.addEventListeners();
    window.addEventListener(`${EVENTS.RESIZE}`, this.handleResize);

    return this;
  }

  /**
   * Remove event listeners
   * @returns {object} TabComponent A reference to the current instance of the class
   * @chainable
   */
  disable() {
    this.removeEventListeners();
    window.removeEventListener(`${EVENTS.RESIZE}`, this.handleResize);
    return this;
  }

  /**
   * Add event listeners based on viewport
   * @private
   */
  addEventListeners() {
    this.removeEventListeners(); // Clean up existing listeners

    if (this.tabs?.offsetParent !== null) {
      this.tabButtons?.forEach((button) => {
        button.addEventListener(EVENTS.CLICK, this.handleTabClick);
      });
    } else {
      this.items?.forEach((item) => {
        item.addEventListener(EVENTS.CLICK, this.handleItemClick);
      });
    }
  }

  /**
   * Remove event listeners
   * @private
   */
  removeEventListeners() {
    this.tabButtons?.forEach((button) => {
      button.removeEventListener(EVENTS.CLICK, this.handleTabClick);
    });

    this.items?.forEach((item) => {
      item.removeEventListener(EVENTS.CLICK, this.handleItemClick);
    });
  }

  /**
   * Handle window resize
   * @private
   */
  handleResize = () => {
    const wasDesktop = !this.isMobile;
    this.isMobile = window.innerWidth < 768;
    const isDesktop = !this.isMobile;

    // Only update if we've crossed the breakpoint
    if (wasDesktop !== isDesktop) {
      this.addEventListeners();

      // Update active states when switching between mobile/desktop
      const activeItem = this.element.querySelector(
        `.${CLASS_NAMES.ITEM}.${CLASS_NAMES.ACTIVE}`
      );
      if (activeItem) {
        const itemId = activeItem.getAttribute('id');
        const correspondingTab = this.element.querySelector(
          `a[href="#${itemId}"]`
        );

        if (isDesktop && correspondingTab) {
          correspondingTab.classList.add(CLASS_NAMES.ACTIVE);
        } else if (!isDesktop) {
          this.tabButtons?.forEach((button) =>
            button.classList.remove(CLASS_NAMES.ACTIVE)
          );
        }
      }
    }
  };

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {object} TabComponent A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.handleTabClick = this.handleTabClick.bind(this);
    this.handleItemClick = this.handleItemClick.bind(this);
    // handleResize is already bound using arrow function
    return this;
  }

  /**
   * Handle tab click events
   * @param {Event} event - the click event object
   */
  handleTabClick(event) {
    event.preventDefault();
    const target = event.currentTarget.getAttribute('href');

    // Remove active classes
    this.tabButtons?.forEach((button) =>
      button.classList.remove(`${CLASS_NAMES.ACTIVE}`)
    );
    this.items?.forEach((item) =>
      item.classList.remove(`${CLASS_NAMES.ACTIVE}`)
    );

    // Add active classes
    event.currentTarget.classList.add(`${CLASS_NAMES.ACTIVE}`);
    this.element.querySelector(target)?.classList.add(`${CLASS_NAMES.ACTIVE}`);
  }

  /**
   * Handle item click events (mobile view)
   * @param {Event} event - the click event object
   */
  handleItemClick(event) {
    const clickedItem = event.currentTarget;
    const itemId = clickedItem.getAttribute('id');

    // Remove active classes
    this.tabButtons?.forEach((button) =>
      button.classList.remove(`${CLASS_NAMES.ACTIVE}`)
    );
    this.items?.forEach((item) =>
      item.classList.remove(`${CLASS_NAMES.ACTIVE}`)
    );

    // Add active classes
    clickedItem.classList.add(`${CLASS_NAMES.ACTIVE}`);
    this.element
      .querySelector(`a[href="#${itemId}"]`)
      ?.classList.add(`${CLASS_NAMES.ACTIVE}`);

    // Smooth scroll
    window.scrollTo({
      top: clickedItem.offsetTop - 65,
      behavior: 'smooth'
    });
  }
}
